import React from 'react';
import PropTypes from 'prop-types';
import {
  compose,
  lifecycle,
  withState,
  withHandlers,
  withProps,
} from 'recompose';
import { connect } from 'react-redux';
import ReactTable from 'react-table';
import { visibility } from 'infrastructure/enhancers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretUp, faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { get } from 'lodash';
import { UIMessage, Loading } from 'components';
import Artwork from 'img/blank-states/search.png';

import TableToolbar from './TableToolbar';
import DataTablePagination from './Pagination';
import TableActions from './TableActions';
import {
  changeSort,
  changePage,
  changeRowsPerPage,
  changeFilters,
  changeSearchText,
} from './actions';
import filterActions from './filterActions';
import {
  setLsPage,
  setLsPageSize,
  setLsSorted,
  setLsFilters,
  getPagingParams,
  getTableMeta,
} from './paging';

import './DataTable.scss';

const selectTableMetaData = (state, { name }) => state.dataTable[name] || {};
const enhance = compose(
  withProps(({ visible, identifier, name }) => ({
    visible: ((visible === undefined || visible === null) && true) || visible,
    localStorageId: identifier || name,
  })),
  visibility,
  connect(
    (state, props) => {
      const metaData = selectTableMetaData(state, props);

      return {
        metaData,
        formatExtraData: {
          ...props.formatExtraData,
        },
      };
    },
    (dispatch, props) => ({
      storePage: page => {
        dispatch(
          changePage({
            name: props.name,
            page,
          })
        );
        if (typeof props.onPageChange === 'function') props.onPageChange();
      },
      storePageSize: pageSize => {
        dispatch(
          changeRowsPerPage({
            name: props.name,
            pageSize,
          })
        );
        if (typeof props.onPageSizeChange === 'function')
          props.onPageSizeChange();
      },
      storeSorted: sorted => {
        dispatch(
          changeSort({
            name: props.name,
            sorted,
          })
        );
        if (typeof props.onSortedChange === 'function') props.onSortedChange();
      },
      storeFilters: filters => {
        dispatch(
          changeFilters({
            name: props.name,
            filters,
          })
        );
      },
      storeSearchText: searchText => {
        dispatch(
          changeSearchText({
            name: props.name,
            searchText,
          })
        );
        if (typeof props.onSearchChanged === 'function')
          props.onSearchChanged();
      },
    })
  ),
  lifecycle({
    componentDidMount() {
      const {
        localStorageId,
        storePage,
        storePageSize,
        storeSorted,
        storeFilters,
      } = this.props;
      const tableMeta = getTableMeta(localStorageId);
      const { page, pageSize, sorted, filters } = tableMeta;
      if (page || page === 0) storePage(page);
      if (pageSize) storePageSize(pageSize);
      if (sorted) storeSorted(sorted);
      if (filters) storeFilters(filters);
    },
  }),
  withState('searchTimeout', 'setSearchTimeout'),
  withHandlers({
    onSearchChanged: props => searchText => {
      props.storeSearchText(searchText);
      if (props.metaData.page !== 0) props.storePage(0);

      if (props.serverManaged) {
        const newMeta = { ...props.metaData, searchText, page: 0 };
        const params = getPagingParams(newMeta);
        clearTimeout(props.searchTimeout);

        const searchTimeout = setTimeout(() => {
          props.fetchData(params);
        }, 500);

        props.setSearchTimeout(searchTimeout);
      }
    },
    onPageChange: props => page => {
      props.storePage(page);
      // Store in LocalStorage
      setLsPage(props.localStorageId, page);
      if (props.serverManaged) {
        const newMeta = { ...props.metaData, page };
        const params = getPagingParams(newMeta);
        props.fetchData(params);
      }
      window.scrollTo(0, 0);
    },
    onPageSizeChange: props => pageSize => {
      props.storePage(0);
      props.storePageSize(pageSize);
      // Store in LocalStorage
      setLsPage(props.localStorageId, 0);
      setLsPageSize(props.localStorageId, pageSize);
      if (props.serverManaged) {
        const newMeta = { ...props.metaData, pageSize, page: 0 };
        const params = getPagingParams(newMeta);
        props.fetchData(params);
      }
    },
    onSortedChange: props => sorted => {
      props.storePage(0);
      props.storeSorted(sorted);
      // Store in LocalStorage
      setLsPage(props.localStorageId, 0);
      setLsSorted(props.localStorageId, sorted);
      if (props.serverManaged) {
        const newMeta = { ...props.metaData, sorted, page: 0 };
        const params = getPagingParams(newMeta);
        props.fetchData(params);
      }
    },
    updateFiltersHandler: props => filter => {
      const meta = props.metaData;
      const newFilters = { ...meta.filters };
      Object.keys(filter).forEach(f => {
        newFilters[f] = filter[f];
      });
      props.storePage(0);
      props.storeFilters(newFilters);
      // Store in LocalStorage
      setLsPage(props.localStorageId, 0);
      setLsFilters(props.localStorageId, newFilters);
      if (props.serverManaged) {
        const newMeta = { ...meta, filters: newFilters, page: 0 };
        const params = getPagingParams(newMeta);
        props.fetchData(params);
      }
    },
  })
);

function getCurrencyValue(currencySetting, value) {
  if (currencySetting !== undefined) {
    return new Intl.NumberFormat(
      currencySetting.locale !== undefined ? currencySetting.locale : 'en-US',
      {
        style: 'currency',
        currency:
          currencySetting.currencyCode !== undefined
            ? currencySetting.currencyCode
            : 'USD',
        minimumFractionDigits:
          currencySetting.minimumFractionDigits !== undefined
            ? currencySetting.minimumFractionDigits
            : 2,
        maximumFractionDigits:
          currencySetting.maximumFractionDigits !== undefined
            ? currencySetting.maximumFractionDigits
            : 2,
      }
    ).format(value !== undefined ? value : 0);
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value !== undefined ? value : 0);
}

function transformCols(cols, actions, actionsColWidth) {
  const processedCols = cols.map(c => {
    const col = {
      ...c,
      Header: (
        <span className="column-name">
          {c.title} <FontAwesomeIcon icon={faCaretUp} className="sort-icon" />
          <FontAwesomeIcon icon={faCaretDown} className="sort-icon" />
        </span>
      ),
      accessor: c.field,
    };

    if (col.headerOverride) {
      col.Header = ({ ...props }) => col.headerOverride(props);
    }

    if (col.render) {
      col.Cell = ({ original, ...rest }) => col.render(original, rest);
    }

    if (c.type && !col.Cell) {
      if (c.type === 'date' || c.type === 'time' || c.type === 'datetime') {
        col.Cell = ({ value }) => {
          if (value instanceof Date) {
            return value.toLocaleDateString();
          }
          return value;
        };
      }

      if (c.type === 'currency') {
        col.Cell = ({ value }) => getCurrencyValue(c.currencySetting, value);
      }

      if (c.type === 'boolean') {
        col.Cell = ({ value }) => (value || false).toString();
      }
    }

    if (!col.Cell) {
      // To support empty values
      col.Cell = ({ value }) => value || '';
    }

    if (col.customSort) {
      col.sortMethod = col.customSort;
    }

    return col;
  });

  const processActionsCols = rowActions => {
    if (!rowActions.length) return [];

    return [
      {
        sortable: false,
        width: actionsColWidth,
        className: 'table-actions',
        /* eslint-disable react/prop-types */
        Cell: ({ original }) =>
          !original.hideRowActions ? (
            <TableActions actions={rowActions} data={original} />
          ) : null,
        /* eslint-enable react/prop-types */
        Header: <span className="column-name">Actions</span>,
        headerClassName: 'table-actions',
      },
    ];
  };

  const additionalCols = processActionsCols(
    actions.filter(
      filterActions({
        selection: false,
      })
    )
  );

  return [...processedCols, ...additionalCols];
}

function transformDefaultSorted(defaultSorted = [], cols) {
  return [
    ...defaultSorted,
    ...cols
      .filter(x => x.defaultSort)
      .map(x => ({
        id: x.id || x.accessor,
        desc: x.defaultSort !== 'asc',
      })),
  ];
}

function processSearch(searchText = '', cols) {
  return data => {
    if (!searchText.length) return data;

    const dataCols = cols.map(x => x.filterable && x.accessor);

    const results = data.filter(x => {
      const r = dataCols.map(c =>
        JSON.stringify(get(x, c) || '')
          .toLowerCase()
          .includes(searchText.toLowerCase())
      );

      return r.filter(c => c).length > 0;
    });

    return results;
  };
}

function dataProcessing({ searchText, cols, serverManaged }) {
  return serverManaged
    ? data => data
    : data => {
        const afterSearchProcessingResult = processSearch(
          searchText,
          cols
        )(data);
        return afterSearchProcessingResult;
      };
}

// TODO: materal-table currently makes changes to the data that makes it back to
// redux.  This stops that.  Temporary fix until I get time to fix correct
const prepareData = data =>
  data.map(x => ({
    ...x,
  }));

const getNumberOfPages = (totalItems, pageSize) =>
  Math.ceil(totalItems / pageSize) || 1;

const DataTable = ({
  actions,
  columns,
  data,
  defaultSorted,
  hasRowSelect,
  metaData,
  onSearchChanged,
  options,
  paginated,
  search,
  selectedRows,
  actionsColWidth,
  totalCount,
  serverManaged,
  filtersConfig,
  updateFiltersHandler,
  loading,
  localStorageId,
  ...props
}) => {
  const finalOptions = {
    emptyRowsWhenPaging: false,
    pageSize: 20,
    pageSizeOptions: [5, 10, 20, 50, 100],
    ...options,
    ...metaData,
  };
  const { searchText } = metaData;

  const cols = transformCols(columns, actions, actionsColWidth);
  const processData = dataProcessing({
    searchText,
    cols,
    serverManaged,
  });
  const parsedTotal = serverManaged ? totalCount : processData(data).length;

  return (
    <ReactTable
      {...props}
      {...finalOptions}
      columns={cols}
      data={prepareData(data)}
      defaultSorted={transformDefaultSorted(defaultSorted, cols)}
      minRows={0}
      showPagination={paginated && parsedTotal > 0}
      PaginationComponent={DataTablePagination}
      FilterComponent={<></>}
      resizable={false}
      resolveData={processData}
      noDataText={
        !loading && (
          <UIMessage
            className="no-results"
            image={Artwork}
            title={options.noDataTitle || 'No Results Found.'}
            message={
              options.noDataMessage ||
              'Please check your search term for spelling and typos, or try removing filters.'
            }
          />
        )
      }
      loadingText={<Loading visible loading />}
      loading={loading}
      filterable
      manual={serverManaged}
      pages={getNumberOfPages(parsedTotal, finalOptions.pageSize)}
    >
      {({ filters, page, pageSize }, makeTable) => (
        <React.Fragment>
          <TableToolbar
            onSearchChanged={onSearchChanged}
            searchText={searchText}
            search={search}
            actions={actions}
            hasRowSelect={hasRowSelect}
            selectedRows={selectedRows}
            paginated={paginated}
            filters={filters}
            filtersConfig={filtersConfig}
            page={page}
            pageSize={pageSize}
            totalCount={parsedTotal}
            updateFilters={updateFiltersHandler}
          />
          <div className="data-table__wrapper">{makeTable()}</div>
        </React.Fragment>
      )}
    </ReactTable>
  );
};

DataTable.propTypes = {
  title: PropTypes.string,
  actions: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.shape(), PropTypes.func])
  ),
  columns: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  data: PropTypes.arrayOf(PropTypes.shape()),
  defaultSorted: PropTypes.arrayOf(PropTypes.shape()),
  hasRowSelect: PropTypes.bool,
  metaData: PropTypes.shape({
    searchText: PropTypes.string,
  }),
  onSearchChanged: PropTypes.func,
  options: PropTypes.shape(),
  paginated: PropTypes.bool,
  search: PropTypes.bool,
  selectedRows: PropTypes.arrayOf(PropTypes.shape({})),
  actionsColWidth: PropTypes.number,
  fetchData: PropTypes.func,
  totalCount: PropTypes.number,
  serverManaged: PropTypes.bool,
  filtersConfig: PropTypes.arrayOf(PropTypes.shape({})),
  updateFiltersHandler: PropTypes.func,
  loading: PropTypes.bool,
  localStorageId: PropTypes.string,
};

DataTable.defaultProps = {
  title: '',
  actions: [],
  data: [],
  defaultSorted: [],
  hasRowSelect: true,
  metaData: {},
  onSearchChanged: () => {},
  options: {},
  paginated: true,
  search: true,
  selectedRows: [],
  actionsColWidth: 180,
  fetchData: () => {},
  totalCount: 0,
  serverManaged: false,
  filtersConfig: [],
  updateFiltersHandler: () => {},
  loading: false,
  localStorageId: '',
};

export default enhance(DataTable);
